import type { PropsWithChildren } from 'react';
import { forwardRef } from 'react';
import { type ButtonVariant } from '@mweb/zappos-ui/Button';
import {
  VideoPlayer as DesignLibraryVideoPlayer,
  VideoPlayerContent,
  VideoPlayerLayout,
  type VideoPlayerLayoutPlacement,
  VideoPlayerOverlay,
  type VideoPlayerOverlayPlacement
} from '@mweb/zappos-ui/VideoPlayer';

import Button from 'components/common/Button';
import type { VideoPlayerProps } from 'components/common/VideoPlayer/VideoPlayer.types';
import MartyLink from 'components/common/MartyLink';

const VideoPlayer = forwardRef<HTMLVideoElement, VideoPlayerProps>((props: VideoPlayerProps, ref) => {
  const {
    variant = 'outlinedReversed',
    heading,
    copy,
    cta,
    url,
    placement = 'center',
    handleCTAAction,
    controls = false,
    muted = true,
    autoPlay = true,
    loop = true,
    playsInline = true,
    backgroundColor,
    videoCtaUrl,
    ...forwardProps
  } = props;

  const hasContent = heading || copy || cta;
  const commonVideoProps = { autoPlay, muted, loop, controls, playsInline, ...forwardProps, ref };

  if (!hasContent) {
    return (
      <Wrapper videoCtaUrl={videoCtaUrl}>
        <DesignLibraryVideoPlayer {...commonVideoProps} />
      </Wrapper>
    );
  }

  const hasCTA = cta && url ? true : false;
  const linkCTA: ButtonVariant[] = ['text', 'textReversed'];
  const action = linkCTA.includes(variant) ? { href: url } : { onClick: () => handleCTAAction?.() };
  const overlayPlacements: VideoPlayerOverlayPlacement[] = ['center', 'center-right', 'bottom-right'];
  const layoutPlacements: VideoPlayerLayoutPlacement[] = ['content-left', 'content-right', 'content-bottom'];
  const contentOverlayPlacement = overlayPlacements.includes(placement as VideoPlayerOverlayPlacement) ? placement : undefined;
  const contentLayoutPlacement = layoutPlacements.includes(placement as VideoPlayerLayoutPlacement) ? placement : undefined;
  const hasOverlayAndCtaUrl = videoCtaUrl && contentOverlayPlacement;
  const commonContentProps = {
    variant,
    heading,
    subHeading: copy,
    // Do not add a cta link if there is a link wrapping the video
    cta: hasCTA && !hasOverlayAndCtaUrl ? <Button {...action}>{cta}</Button> : undefined
  };

  if (contentOverlayPlacement) {
    return (
      <Wrapper videoCtaUrl={videoCtaUrl}>
        <DesignLibraryVideoPlayer {...commonVideoProps}>
          <VideoPlayerOverlay placement={contentOverlayPlacement as VideoPlayerOverlayPlacement}>
            <VideoPlayerContent {...commonContentProps} />
          </VideoPlayerOverlay>
        </DesignLibraryVideoPlayer>
      </Wrapper>
    );
  }

  if (contentLayoutPlacement) {
    return (
      <VideoPlayerLayout
        contentBackground={backgroundColor}
        contentVariant={variant}
        placement={placement as VideoPlayerLayoutPlacement}
        videoPlayer={
          <Wrapper videoCtaUrl={videoCtaUrl}>
            <DesignLibraryVideoPlayer {...commonVideoProps} />
          </Wrapper>
        }
        videoPlayerContent={<VideoPlayerContent {...commonContentProps} />}
      />
    );
  }

  return (
    <Wrapper videoCtaUrl={videoCtaUrl}>
      <DesignLibraryVideoPlayer {...commonVideoProps} />
    </Wrapper>
  );
});

export default VideoPlayer;

interface WrapperProps extends PropsWithChildren {
  videoCtaUrl: string | undefined;
}

const Wrapper = (props: WrapperProps) => {
  const { videoCtaUrl, children } = props;
  const videoLinkAriaLabel = 'View product details';

  if (!videoCtaUrl) {
    return children;
  }

  return (
    <MartyLink to={videoCtaUrl} aria-label={videoLinkAriaLabel}>
      {children}
    </MartyLink>
  );
};
